import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import objectSupport from 'dayjs/plugin/objectSupport';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

import 'dayjs/locale/fr';
import { DateRange, TimeRange } from '@veone/ngx-utilities/models';
import { TimeRangeName } from '~core/models/shared.models';

@Injectable()
export class DateService {
  private readonly _dayjs: typeof dayjs;

  constructor() {
    dayjs.extend(objectSupport);
    dayjs.extend(duration);
    dayjs.extend(relativeTime);
    dayjs.extend(advancedFormat);
    dayjs.extend(isoWeek);
    dayjs.extend(isSameOrBefore);
    dayjs.extend(isSameOrAfter);
    dayjs.locale('fr');

    this._dayjs = dayjs;
  }

  /**
   * Returns the configured dayjs instance (with locale + plugins).
   */
  get instance(): typeof dayjs {
    dayjs.locale('fr');
    return dayjs;
  }

  getStartOfDay = (date?: Date | dayjs.Dayjs): string => {
    return this._dayjs(date).startOf('day').format();
  };

  getEndOfDay = (date?: Date | dayjs.Dayjs): string => {
    return this._dayjs(date).endOf('day').format();
  };

  filterCurrentWeekOnly = (date: Date | null): boolean => {
    if (!date) return false;

    const today = this._dayjs();
    const startOfWeek = today.startOf('isoWeek');
    const endOfWeek = today.endOf('isoWeek');

    const current = dayjs(date);

    return (
      current.isSameOrAfter(startOfWeek, 'day') &&
      current.isSameOrBefore(endOfWeek, 'day')
    );
  };

  generateTimeRange(timeOption: TimeRangeName): TimeRange {
    let result: TimeRange;
    const now = dayjs();
    switch (timeOption) {
      case TimeRangeName.LAST_THREE_MINUTE:
        result = {
          start: now.clone().subtract(3, 'minute').format(),
          end: now.clone().format()
        };
        break;
      case TimeRangeName.LAST_HOUR:
        result = {
          start: now.clone().subtract(60, 'minute').format(),
          end: now.clone().format()
        };
        break;
      case TimeRangeName.LAST_DAY:
        result = {
          start: now.clone().subtract(1, 'day').format(),
          end: now.clone().format()
        };
        break;
      case TimeRangeName.LAST_THREE_DAYS:
        result = {
          start: now.clone().subtract(3, 'day').format(),
          end: now.clone().format()
        };
        break;
      case TimeRangeName.LAST_WEEK:
        result = {
          start: now.clone().subtract(7, 'day').format(),
          end: now.clone().format()
        };
        break;
      case TimeRangeName.LAST_MONTH:
        result = {
          start: now.clone().subtract(1, 'month').format(),
          end: now.clone().format()
        };
        break;
      case TimeRangeName.LAST_THREE_MONTHS:
        result = {
          start: now.clone().subtract(3, 'month').format(),
          end: now.clone().format()
        };
        break;

      case TimeRangeName.LAST_YEAR:
        result = {
          start: now.clone().subtract(1, 'year').format(),
          end: now.clone().format()
        };
        break;

      case TimeRangeName.ANYTIME:
        result = {
          start: now.clone().subtract(1000, 'year').format(),
          end: now.clone().format()
        };
        break;

      case TimeRangeName.CURRENT_WEEK:
        result = {
          start: now.clone().startOf('week').format(),
          end: now.clone().endOf('week').format()
        };
        break;

      case TimeRangeName.CURRENT_MONTH:
        result = {
          start: now.clone().startOf('month').format(),
          end: now.clone().endOf('month').format()
        };
        break;

      case TimeRangeName.CURRENT_YEAR:
        result = {
          start: now.clone().startOf('year').format(),
          end: now.clone().endOf('year').format()
        };
        break;

      default:
        result = {
          start: now.clone().startOf('day').format(),
          end: now.clone().endOf('day').format()
        };
        break;
    }

    return result;
  }

  timeRangeToDateRange(value: TimeRange | null): DateRange<dayjs.Dayjs> | null {
    if (!value) return null;
    const { end, start } = value;
    const endDate = this.instance(end);
    const startDate = this.instance(start);

    return {
      start: startDate.isValid() ? startDate : null,
      end: endDate.isValid() ? endDate : null
    } as DateRange<dayjs.Dayjs>;
  }
}
